// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import {
  RouterModule,
  Routes,
  UrlSegment,
  provideRouter,
  withComponentInputBinding,
} from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PlannedActivitiesPageComponent } from 'src/app/modules/portal/components/planned-activities-page/planned-activities-page.component';
import { environment } from 'src/environments/environment';
import { siemensHealthineersGuard } from './guards/siemens-healthineers/siemens-healthineers.guard';
import { CoilPhoneViewComponent } from './modules/coils/components/coil-phone-view/coil-phone-view.component';
import { MrqDetailsComponent } from './modules/patternmatch/components/mrq-details/mrq-details.component';
import { AdvancedSearchComponent } from './modules/portal/components/advanced-search/advanced-search.component';
import { EmbeddedViewComponent } from './modules/portal/components/embeddedView/embeddedView.component';
import { LandingPageComponent } from './modules/portal/components/landing-page/landingPage.component';
import { PortalComponent } from './modules/portal/components/portal/portal.component';
import { ReleaseNotesComponent } from './modules/portal/components/release-notes/release-notes.component';
import { ServiceTicketsPageComponent } from './modules/portal/components/service-tickets-page/service-tickets-page.component';
import { SettingsComponent } from './modules/portal/components/settings/settings.component';
import { SystemOverviewComponent } from './modules/portal/components/system-overview/system-overview.component';
import { SystemRootComponent } from './modules/portal/components/system-root/system-root.component';
import { CtsGantryEventDetailsComponent } from './modules/ct-status/components/cts-gantry-event-details/cts-gantry-event-details.component';
import { CtsGantryLogDetailComponent } from './modules/ct-status/components/cts-gantry-log-detail/cts-gantry-log-detail.component';
import { SparePartsPageComponent } from './modules/spare-parts/spare-parts-page/spare-parts-page.component';
import { UnauthorizedComponent } from './modules/portal/components/unauthorized/unauthorized.component';

const routes: Routes = [
  {
    canActivate: [MsalGuard, siemensHealthineersGuard],
    path: '',
    component: PortalComponent,
    children: [
      {
        path: '',
        component: LandingPageComponent,
      },
      {
        path: 'unauthorized',
        component: UnauthorizedComponent,
      },
      {
        path: 'releaseNotes',
        component: ReleaseNotesComponent,
      },
      {
        path: 'settings',
        component: SettingsComponent,
      },
      {
        path: 'advancedSearch',
        component: AdvancedSearchComponent,
      },
      {
        path: 'system/:systemMaterialNumber/:systemSerialNumber',
        component: SystemRootComponent,
        children: [
          { path: 'coil-phone-view', component: CoilPhoneViewComponent },
          {
            path: 'tickets',
            component: ServiceTicketsPageComponent,
          },
          {
            path: 'activities',
            component: PlannedActivitiesPageComponent,
          },
          {
            path: 'details',
            component: MrqDetailsComponent,
            data: { hideAppFooter: false, hideSystemDetails: true },
          },
          // {
          //   path: 'docbox',
          //   component: DocBoxPageComponent,
          // },
          {
            path: '',
            component: SystemOverviewComponent,
          },
          // special components (i.e. external apps put into an iframe)
          {
            path: 'component/dicommonitor',
            component: EmbeddedViewComponent,
            data: {
              externalWebApp:
                '/dicommonitor/dicommonitor?systemserialnumber={systemSerialNumber}&systemmaterialnumber={systemMaterialNumber}&modalityid={modalityId}&dateFormat={dateFormat}&token={idToken}',
            },
          },
          {
            path: 'component/detectormonitor',
            component: EmbeddedViewComponent,
            data: {
              externalWebApp:
                '/detectormonitor/detectormonitor?systemserialnumber={systemSerialNumber}&systemmaterialnumber={systemMaterialNumber}&ticketnumber={ticketNumber}&modalityId={modalityId}&dateFormat={dateFormat}&token={idToken}',
            },
          },

          // takes any other component (id)
          {
            path: 'component/:component',
            component: EmbeddedViewComponent,
          },
          {
            path: 'ct-status/events',
            component: CtsGantryEventDetailsComponent,
          },
          {
            path: 'ct-status/logs/:eventSource',
            component: CtsGantryLogDetailComponent,
          },
          {
            path: 'spareParts/:tab',
            component: SparePartsPageComponent,
          },
        ],
      },
    ],
  },
  {
    matcher: (segments: UrlSegment[]) => {
      if (
        segments.length === 0 ||
        // below route is for backward compatibility ONL
        // we should remove this once we are sure we can
        (segments[0].path === 'portal' && segments.length)
      ) {
        return { consumed: segments };
      }
      return null;
    },
    canActivate: [MsalGuard, siemensHealthineersGuard],
    component: PortalComponent,
    children: [
      {
        path: '',
        component: LandingPageComponent,
      },
    ],
  },
  { path: '**', redirectTo: '/' },
];

@NgModule({
  exports: [RouterModule],
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: !environment.production,
      bindToComponentInputs: true,
      paramsInheritanceStrategy: 'always', // subroutes inherit parent route params
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppRoutingModule {}

// required for AOT compilation

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
